/**
 * App entry.
 *
 * Two debug tiers exist, and they are deliberately separate (plan §11):
 *   debug-dom  `?debug=1` before START — every state reachable with no camera and no WebGL.
 *   debug-3d   once the AR session is running — HUD, transform tuner, forced outcomes.
 *
 * Screen visuals here are structural. T5 rebuilds them against the reference PNGs in `info/`.
 */

import './core/ui/tokens.css';
import { loadConfig, saveConfig } from './core/config/store';
import type { Config, FigureId } from './core/config/defaults';
import {
  StateMachine,
  STATES,
  showsLostOverlay,
  suppressesAffordance,
  type AppEvent,
  type AppState,
} from './core/state/machine';
import { detectEnvironment, probeCameraPermission, type UnsupportedReason } from './ar/environment';
// `ArController` is imported dynamically in `startAr()` — a static import would pull three.js and
// the whole AR graph into the first-paint chunk, and BOOT must stay plain DOM.
import type { ArController } from './ar/arController';
import { MockPrizeService } from './services/MockPrizeService';
import { SessionUuidIdentity } from './services/SessionUuidIdentity';
import {
  prepareCoupon,
  releaseCoupon,
  saveCoupon,
  type PreparedCoupon,
  type SaveOutcome,
} from './services/couponSave';
import type { DrawResult } from './core/contracts';

const { config, warnings, corrupt, wasReset } = loadConfig();

const root = document.querySelector<HTMLDivElement>('#app');
if (!root) throw new Error('#app is missing from index.html');

// MindAR appends its own <video> and canvases here. Layout and stacking live in tokens.css —
// see the comment there; getting the z-index wrong makes every control silently unclickable.
const arContainer = document.createElement('div');
arContainer.id = 'ar-container';
document.body.insertBefore(arContainer, root);

const params = new URLSearchParams(location.search);
const debugEnabled = params.get(config.DEBUG.param) === '1';

const env = detectEnvironment();
const prize = new MockPrizeService(config);
const identity = new SessionUuidIdentity();

let ar: ArController | null = null;
const machine = new StateMachine({ isSessionRunning: () => ar !== null });

let loadProgress = 0;
let targetVisible = true;
let permissionHint: 'none' | 'transient' | 'sticky' | UnsupportedReason = 'none';
let lastDraw: DrawResult | null = null;
let busy = false;

let idleTimer: number | null = null;

// --- coupon save (T6) ------------------------------------------------------
// The File is built on entering RESULT, never in the tap handler: an `await` between the tap and
// `navigator.share()` consumes transient activation and iOS rejects with NotAllowedError (plan §9).
let coupon: PreparedCoupon | null = null;
let couponState: 'idle' | 'preparing' | 'ready' | 'failed' = 'idle';
let couponSheetOpen = false;
/** Guards against a second RESULT entry landing while the first fetch is still in flight. */
let couponPrepare: AbortController | null = null;

/**
 * Everything side-effectful lives here, and it is invoked from the LAST line of the module.
 *
 * Not stylistic: `render()` reaches `debugPanel()`, which reads module-level `const`s declared
 * further down. Calling render at the point of declaration put those in the temporal dead zone
 * and threw "Cannot access 'X' before initialization" — a blank screen in the production bundle
 * only, because minification renames the symbol and buries the cause.
 */
function bootstrap(): void {
  machine.onTransition((t) => {
    // Entering RESULT starts the fetch; leaving it tears down the object URL and any open sheet.
    // Re-entering RESULT (round 2) re-prepares, because the previous coupon was already released.
    if (t.to === 'RESULT') void prepareCouponForResult();
    else if (t.from === 'RESULT') discardCoupon();
    render();
  });

  // Automation hook for the playwright flow check against `?debug=1` (project CLAUDE.md §3).
  // Debug builds only — never exposed in a normal session.
  if (debugEnabled) {
    Object.defineProperty(window, '__APP__', {
      value: {
        machine,
        config,
        env,
        get ar(): ArController | null {
          return ar;
        },
        get targetVisible(): boolean {
          return targetVisible;
        },
      },
    });
  }

  if (!env.ok && env.reason) {
    permissionHint = env.reason;
    machine.forceState('UNSUPPORTED');
  }
  render();

  if (corrupt) showNotice(config.COPY.common.configCorrupt);
  else if (warnings.length > 0 && debugEnabled) showNotice(warnings.join('\n'));
  if (wasReset && debugEnabled) showNotice('설정을 초기화했습니다.');

  // An abandoned session must not leave the next visitor mid-flow.
  for (const evt of ['pointerdown', 'visibilitychange'] as const) {
    document.addEventListener(evt, resetIdleTimer);
  }
  resetIdleTimer();
}

function resetIdleTimer(): void {
  if (idleTimer !== null) clearTimeout(idleTimer);
  const ms = config.TIMING.idleTimeoutMs;
  if (ms === null) return;
  idleTimer = window.setTimeout(() => {
    if (machine.state !== 'BOOT') {
      showNotice(config.COPY.common.idleTimeout);
      machine.send('IDLE_TIMEOUT');
    }
  }, ms);
}

// ---------------------------------------------------------------------------
// Flow
// ---------------------------------------------------------------------------

async function handleStart(): Promise<void> {
  if (busy) return;
  busy = true;
  try {
    // The machine routes straight to SCANNING when a session already exists: re-probing
    // getUserMedia while MindAR holds the stream can black out the feed on iOS.
    machine.send('START');
    if (machine.state !== 'PERMISSION') return;

    const outcome = await probeCameraPermission();
    if (outcome.state === 'unavailable') {
      permissionHint = outcome.reason;
      machine.send('UNSUPPORTED_DETECTED');
      return;
    }
    if (outcome.state === 'denied') {
      permissionHint = outcome.sticky ? 'sticky' : 'transient';
      machine.send('PERMISSION_DENIED');
      return;
    }

    permissionHint = 'none';
    machine.send('PERMISSION_GRANTED');
    await startAr();
  } finally {
    busy = false;
  }
}

async function startAr(): Promise<void> {
  try {
    const { ArController: Controller } = await import('./ar/arController');
    ar = await Controller.start(
      arContainer,
      config,
      {
        machine,
        onProgress: (ratio) => {
          loadProgress = ratio;
          if (machine.state === 'SCANNING') render();
        },
        onTargetVisibility: (visible) => {
          targetVisible = visible;
          render();
        },
        onDrawResult: (result) => {
          lastDraw = result;
        },
        onCameraSuspended: () => showNotice(config.COPY.common.cameraSuspended),
        onError: (error) => {
          console.error('[ar]', error);
          showNotice(error.message);
        },
      },
      prize,
      identity,
    );
    render();
  } catch (error) {
    console.error('[ar] failed to start', error);
    showNotice(config.COPY.common.loadFailed);
    machine.forceState('BOOT');
  }
}

// ---------------------------------------------------------------------------
// Coupon save (T6, plan §9)
// ---------------------------------------------------------------------------

async function prepareCouponForResult(): Promise<void> {
  discardCoupon();
  couponState = 'preparing';

  // debug-dom jumps straight to RESULT with no draw behind it. Falling back to the configured
  // sample keeps the whole save path exercisable with no camera, which is the point of the tier.
  const win = lastDraw?.status === 'win' ? lastDraw : null;

  const abort = new AbortController();
  couponPrepare = abort;
  try {
    const prepared = await prepareCoupon({
      url: win?.couponUrl ?? config.ASSET.coupon,
      fileNamePattern: config.COUPON.fileName,
      couponCode: win?.couponCode ?? 'DEBUG',
      isIos: env.isIos,
      iosMode: config.COUPON.iosMode,
      signal: abort.signal,
    });
    // A newer RESULT entry superseded this one while the fetch was in flight.
    if (couponPrepare !== abort) {
      releaseCoupon(prepared);
      return;
    }
    coupon = prepared;
    couponState = 'ready';
  } catch (error) {
    if (abort.signal.aborted) return;
    console.error('[coupon] prepare failed', error);
    couponState = 'failed';
  } finally {
    if (couponPrepare === abort) couponPrepare = null;
  }
  render();
}

function discardCoupon(): void {
  couponPrepare?.abort();
  couponPrepare = null;
  releaseCoupon(coupon);
  coupon = null;
  couponState = 'idle';
  couponSheetOpen = false;
}

/**
 * The tap handler. Nothing may be awaited before `saveCoupon()` — it reaches `navigator.share()`
 * synchronously, and the activation granted by this tap has to survive that far.
 */
function handleCouponSave(): void {
  const prepared = coupon;
  if (!prepared) return;

  if (prepared.strategy === 'longPress') {
    couponSheetOpen = true;
    render();
    return;
  }

  void saveCoupon(prepared).then(applySaveOutcome);
}

function applySaveOutcome(outcome: SaveOutcome): void {
  switch (outcome.status) {
    case 'saved':
      machine.send('COUPON_SAVED');
      break;

    case 'downloaded':
      // `<a download>` fires silently — without this toast nothing on screen acknowledges the tap.
      // It is a confirmation of hand-off, not of the file landing: the browser exposes no signal
      // for that, so a blocked download would still read as success here.
      showNotice(config.COPY.result.saved, 'ok', config.COUPON.savedToastMs);
      machine.send('COUPON_SAVED');
      break;

    case 'handedOff':
      // The sheet carries its own "저장 완료" affordance; nothing to do here.
      break;

    case 'cancelled':
      // Share sheet dismissed. Stay in RESULT silently so the user can tap again (plan §9).
      break;

    case 'failed':
      console.error('[coupon] save failed', outcome.error);
      showNotice(config.COPY.result.saveFailed);
      break;
  }
}

/**
 * The iOS route (`COUPON.iosMode = 'layer'`): show the coupon full-screen so it can be long-pressed
 * into Photos. A web page has no other way to reach the camera roll when sharing is off.
 */
function couponSheet(prepared: PreparedCoupon, c: Config): HTMLElement {
  const sheet = el('div', 'coupon-sheet');

  const close = document.createElement('button');
  close.className = 'coupon-sheet__close';
  close.type = 'button';
  close.textContent = '✕';
  close.setAttribute('aria-label', c.COPY.result.close);
  close.addEventListener('click', () => {
    couponSheetOpen = false;
    render();
  });

  const img = document.createElement('img');
  img.className = 'coupon-sheet__img';
  img.src = prepared.objectUrl;
  img.alt = c.COPY.result.couponAlt;

  const actions = el('div', 'coupon-sheet__actions');
  actions.append(
    button(c.COPY.result.confirmSaved, () => {
      couponSheetOpen = false;
      machine.send('COUPON_SAVED');
    }),
  );

  sheet.append(close, heading(c.COPY.result.saveLayerHeading), img, caption(c.COPY.result.longPressGuide), actions);
  return sheet;
}

/** RESULT's primary control, which depends on how far preparation got. */
function couponAction(c: Config): HTMLButtonElement {
  if (couponState === 'failed') {
    return button(c.COPY.result.retry, () => void prepareCouponForResult(), true);
  }
  const ready = couponState === 'ready' && coupon !== null;
  return button(ready ? c.COPY.result.download : c.COPY.result.preparing, handleCouponSave, false, !ready);
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function render(): void {
  if (!root) return;
  root.replaceChildren(renderScreen(machine.state, config));
  if (couponSheetOpen && coupon) root.append(couponSheet(coupon, config));
  if (showsLostOverlay(machine.state) && !targetVisible) root.append(lostOverlay());
  if (debugEnabled) {
    root.append(debugPanel());
    // Deferred so layout has settled before hit-testing.
    requestAnimationFrame(assertControlsHittable);
  }
}

let lastHitTestWarning = '';

/**
 * Verify every visible control is actually the topmost element at its own centre.
 *
 * This exists because a transparent full-screen container once sat above the whole UI: the
 * buttons rendered perfectly and swallowed every tap, and it shipped because the automated flow
 * check drove the state machine directly instead of clicking. Nothing about the DOM, the CSS or
 * the screenshots looked wrong — only hit-testing reveals it.
 *
 * Debug builds only, but it runs on the phone too, which is where layering surprises actually happen.
 */
function assertControlsHittable(): void {
  if (!root) return;
  const problems: string[] = [];
  // While the coupon sheet is up it is SUPPOSED to cover the screen below it, so only its own
  // controls are meaningful to test. Without this the check reports the RESULT button as blocked.
  const sheet = root.querySelector('.coupon-sheet');
  for (const control of root.querySelectorAll<HTMLElement>('button')) {
    if (control.closest('[data-debug-panel]')) continue;
    if (sheet && !sheet.contains(control)) continue;
    const r = control.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    if (hit !== control && !control.contains(hit)) {
      const id = hit ? `<${hit.tagName.toLowerCase()}${hit.id ? `#${hit.id}` : ''}>` : 'nothing';
      problems.push(`"${control.textContent ?? ''}" is covered by ${id}`);
    }
  }
  const message = problems.join('\n');
  if (message === lastHitTestWarning) return;
  lastHitTestWarning = message;
  if (message) showNotice(`탭 차단 감지\n${message}`);
}

function renderScreen(state: AppState, c: Config): HTMLElement {
  const layer = el('div', 'layer');
  const body = el('div', 'stack');
  layer.append(body);

  // A lost target must also disable the affordance, or the user reaches PLAY with an invisible
  // machine and every tap is silently rejected by the anchor-visibility guard.
  const blocked = !targetVisible && suppressesAffordance(state);

  switch (state) {
    case 'BOOT':
      layer.classList.add('layer--opaque');
      body.append(title(c.COPY.boot.titleEn, c.COPY.boot.titleKo), button(c.COPY.boot.start, () => void handleStart()));
      break;

    case 'UNSUPPORTED': {
      layer.classList.add('layer--opaque');
      const reasonCopy: Record<string, string> = {
        inAppBrowser: c.COPY.unsupported.inAppBrowser,
        noWebgl: c.COPY.unsupported.noWebgl,
        insecure: c.COPY.unsupported.insecure,
        noCamera: c.COPY.unsupported.noCamera,
      };
      body.append(
        heading(c.COPY.unsupported.heading),
        caption(reasonCopy[permissionHint] ?? c.COPY.unsupported.inAppBrowser),
        button(c.COPY.unsupported.copyUrl, () => void copyAppUrl(), true),
      );
      break;
    }

    case 'PERMISSION':
      layer.classList.add('layer--opaque');
      body.append(caption(c.COPY.permission.prompt));
      break;

    // ---- states rendered over the live camera: captions go to the BOTTOM ----
    case 'SCANNING':
      body.classList.add('stack--bottom');
      body.append(
        bottomCaption(
          loadProgress < 1
            ? `${c.COPY.scanning.line1}\n${c.COPY.scanning.loading} ${Math.round(loadProgress * 100)}%`
            : `${c.COPY.scanning.line1}\n${c.COPY.scanning.line2}`,
        ),
      );
      break;

    case 'INTRO':
      body.classList.add('stack--bottom');
      body.append(bottomCaption(c.COPY.intro.caption));
      break;

    case 'JOIN_GATE':
      layer.classList.add('layer--scrim');
      body.append(heading(c.COPY.joinGate.heading), button(c.COPY.joinGate.start, () => machine.send('JOIN'), false, blocked));
      break;

    case 'PLAY':
      // The coin is in the 3D scene and the user must be able to tap it anywhere on screen.
      // Bottom placement keeps the text off the machine; pointer-events stay off throughout.
      body.classList.add('stack--bottom');
      body.append(bottomCaption(c.COPY.play.hint));
      break;

    case 'RESOLVING':
      body.classList.add('stack--bottom');
      body.append(bottomCaption(c.COPY.resolving.caption));
      break;

    case 'REVEAL':
      break;

    case 'RESULT':
      layer.classList.add('layer--scrim');
      body.append(
        title(c.COPY.result.brandEn, c.COPY.result.brandKo),
        caption(couponState === 'failed' ? c.COPY.result.saveFailed : c.COPY.result.subhead),
        couponAction(c),
      );
      break;

    case 'DONE':
      layer.classList.add('layer--scrim');
      body.append(heading(c.COPY.done.heading), caption(c.COPY.done.tapToRestart));
      layer.style.pointerEvents = 'auto';
      layer.addEventListener('click', () => machine.send('RESTART'), { once: true });
      break;
  }

  // Also on BOOT: the spec says a denial returns to the entry screen without entering AR, but
  // rendering the reason only on PERMISSION meant the user bounced back to BOOT with no
  // explanation at all — indistinguishable from "the START button is broken".
  if ((state === 'PERMISSION' || state === 'BOOT') && permissionHint !== 'none') {
    appendPermissionRecovery(body, c);
  }
  return layer;
}

function appendPermissionRecovery(body: HTMLElement, c: Config): void {
  if (permissionHint === 'inAppBrowser' || permissionHint === 'noWebgl' || permissionHint === 'insecure' || permissionHint === 'noCamera') {
    return; // handled by the UNSUPPORTED screen
  }
  if (permissionHint === 'sticky') {
    // An in-page retry can never re-prompt once the origin is blocked.
    body.append(
      caption(env.isIos ? c.COPY.permission.deniedStickyIos : c.COPY.permission.deniedStickyAndroid),
      button(c.COPY.permission.reload, () => location.reload()),
    );
  } else if (permissionHint === 'transient') {
    body.append(caption(c.COPY.permission.deniedOnce), button(c.COPY.permission.retry, () => void handleStart()));
  }
}

function lostOverlay(): HTMLElement {
  const box = el('div', 'notice');
  box.textContent = config.COPY.common.targetLost;
  box.style.background = 'rgba(4,20,14,0.85)';
  return box;
}

// ---------------------------------------------------------------------------
// Debug
// ---------------------------------------------------------------------------

const ALL_EVENTS: readonly AppEvent[] = [
  'START', 'UNSUPPORTED_DETECTED', 'PERMISSION_GRANTED', 'PERMISSION_DENIED', 'TARGET_READY',
  'INTRO_END', 'JOIN', 'COIN_HIT', 'DRAW_OK', 'DRAW_FAIL', 'RETRY', 'CAN_STANDING', 'COUPON_SAVED',
  'RESTART', 'IDLE_TIMEOUT',
];

/** HUD sampling period. Fast enough to read while moving, slow enough not to cost the tracker. */
const HUD_REFRESH_MS = 100;

/** Debug notices auto-dismiss: one left on screen during a recording is one covering the target. */
const REC_NOTICE_MS = 2500;
const MM_PER_M = 1000;
/** FOV is stored to one decimal — calibration does not resolve finer than that. */
const FOV_DECIMALS = 10;

/** Recorder scenario id -> chip caption. The id is what appears in the report. */
const SCENARIO_CAPTIONS: readonly (readonly [string, string])[] = [
  ['still', '정지'],
  ['distance', '원거리'],
  ['rotation', '회전'],
];

/**
 * Copies the whole report — every segment recorded so far — in one go.
 *
 * The clipboard call must be the first thing after the tap: awaiting anything before it costs the
 * page its transient activation and iOS silently refuses (the same lesson as the coupon `File`).
 * The fallback is a textarea rather than `window.prompt`, which cannot show a report this long.
 */
async function copyRecording(): Promise<void> {
  const text = ar?.recorderReport() ?? '';
  if (!text.includes('## seg')) {
    showNotice('기록된 구간이 없습니다. 먼저 ● 버튼으로 녹화하세요.', 'error', REC_NOTICE_MS);
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    showNotice(`복사했습니다 (${text.length}자)`, 'ok', REC_NOTICE_MS);
  } catch {
    showReportFallback(text);
  }
}

/**
 * Camera FOV calibration, one tap and one ruler.
 *
 * MindAR fits every pose with a hardcoded 45° camera (`src/ar/intrinsics.ts`). The error does not
 * move the card — it tilts everything standing off it, by an amount that changes with the viewing
 * angle, which is what "the machine leans when I move sideways" is. The estimated depth carries the
 * same focal-length error exactly, so measuring one distance is enough to solve for the real FOV.
 *
 * Held deliberately behind a confirm: it writes a config value that only takes effect on reload.
 */
function calibrateFov(): void {
  const entered = window.prompt('카드에서 카메라까지의 실제 거리를 자로 재서 mm 단위로 입력하세요 (예: 300)');
  if (entered === null) return;
  const measuredMm = Number(entered.trim());
  if (!Number.isFinite(measuredMm) || measuredMm <= 0) {
    showNotice('숫자(mm)를 입력하세요');
    return;
  }
  let calibrated: number | null;
  try {
    calibrated = ar?.calibrateVerticalFov(measuredMm / MM_PER_M) ?? null;
  } catch (error) {
    showNotice(`계산 실패: ${error instanceof Error ? error.message : String(error)}`);
    return;
  }
  if (calibrated === null) {
    showNotice('타겟을 인식한 상태에서 눌러야 합니다 (카드를 비춘 채로)');
    return;
  }
  const rounded = Math.round(calibrated * FOV_DECIMALS) / FOV_DECIMALS;
  const current = config.MINDAR.cameraVerticalFovDeg;
  if (!window.confirm(`실측 화각 ${rounded}° (현재 ${current}°)\n저장하고 새로고침할까요?`)) return;
  config.MINDAR.cameraVerticalFovDeg = rounded;
  saveConfig(config);
  location.reload();
}

/** Clipboard refused (insecure context, permission, older WebView): let the user select it by hand. */
function showReportFallback(text: string): void {
  const panel = document.querySelector('[data-debug-panel]');
  if (!panel) return;
  const box = document.createElement('textarea');
  box.value = text;
  box.readOnly = true;
  box.style.cssText = 'width:100%;height:30vh;font:11px/1.4 ui-monospace,monospace;margin-top:6px';
  panel.prepend(box);
  box.focus();
  box.select();
  showNotice('클립보드를 쓸 수 없어 직접 선택·복사해야 합니다');
}

function debugPanel(): HTMLElement {
  const panel = el('div');
  panel.dataset['debugPanel'] = '';
  panel.style.cssText =
    'position:fixed;left:0;right:0;bottom:0;z-index:99;background:rgba(0,0,0,.86);color:#9fe;' +
    'font:11px/1.5 ui-monospace,monospace;padding:8px;pointer-events:auto;max-height:46vh;overflow-y:auto';

  const status = el('div');
  status.style.marginBottom = '6px';
  status.textContent =
    `state=${machine.state} tier=${ar ? 'debug-3d' : 'debug-dom'} ` +
    `lost=${!targetVisible} draw=${lastDraw?.status ?? '-'}`;
  panel.append(status);

  if (ar) {
    const session = ar;
    const hud = el('div');
    hud.style.cssText = 'margin-bottom:6px;color:#cfe;white-space:pre-wrap';
    hud.textContent = JSON.stringify(session.hud());
    panel.append(hud);

    /**
     * The HUD is only useful while the phone is MOVING — pose rate, pose age, drift. Built once per
     * state render it showed whatever those were at the last transition, which is unreadable as a
     * tuning aid and is why "the model trails" could not be attributed to a stage.
     *
     * Refreshed on a timer rather than every frame on purpose: `hud()` walks the scene graph for
     * `ghostNodeReport()`, and the tracker shares this thread with us — instrumentation that steals
     * main-thread time would inflate the very latency it is here to measure. Peaks are captured in
     * the frame loop (`maxAgeMs`), not by sampling faster here. Self-cancels when `render()`
     * replaces the panel.
     */
    // A recording auto-stops after its duration, and the phone is pointed at the target rather than
    // at the HUD — without this the user has no idea when the manoeuvre can end.
    let wasRecording = false;
    const tick = (): void => {
      if (!hud.isConnected) return;
      const recording = session.recorderStatus()?.recording === true;
      if (wasRecording && !recording) showNotice('기록 완료 — [복사]로 가져가세요', 'ok', REC_NOTICE_MS);
      wasRecording = recording;
      hud.textContent = JSON.stringify(session.hud());
      window.setTimeout(() => requestAnimationFrame(tick), HUD_REFRESH_MS);
    };
    window.setTimeout(() => requestAnimationFrame(tick), HUD_REFRESH_MS);
  }

  panel.append(chipRow('events', ALL_EVENTS.map((e) => chip(e, machine.can(e), () => machine.send(e)))));
  panel.append(chipRow('jump', STATES.map((s) => chip(s, true, () => machine.forceState(s)))));
  panel.append(
    chipRow('sim', [
      chip(targetVisible ? 'target: FOUND' : 'target: LOST', true, () => {
        targetVisible = !targetVisible;
        render();
      }),
      chip(prize.forceFailure ? 'draw: FAIL' : 'draw: OK', true, () => {
        prize.forceFailure = !prize.forceFailure;
        render();
      }),
      ...(['A', 'B', 'C'] as FigureId[]).map((id) =>
        chip(`win ${id}`, ar !== null, () => {
          ar?.forceWin(id);
          machine.send('DRAW_OK');
        }),
      ),
      // The iOS save layer is unreachable on Android and desktop — the strategy branch sends them
      // to <a download> — so plan §11's "complete the whole flow with no camera" needs a way in.
      chip(couponSheetOpen ? 'sheet: OPEN' : 'coupon sheet', coupon !== null, () => {
        couponSheetOpen = !couponSheetOpen;
        render();
      }),
      chip('assert anchor', ar !== null, () => {
        try {
          ar?.assertAnchorUntouched();
          showNotice('anchor.group is untouched ✓');
        } catch (err) {
          showNotice(String(err));
        }
      }),
    ]),
  );
  // Three labelled runs, one report. The scenarios are the three device complaints: a report that
  // does not say which manoeuvre produced it cannot separate them.
  panel.append(
    chipRow('rec', [
      ...SCENARIO_CAPTIONS.map(([id, caption]) =>
        chip(`● ${caption}`, ar !== null, () => {
          ar?.startRecording(id);
          showNotice(`${caption} 기록 중… ${config.DEBUG.recorder.durationS}초`, 'ok', REC_NOTICE_MS);
        }),
      ),
      chip('■ 중지', ar !== null, () => ar?.stopRecording()),
      chip('복사', ar !== null, () => void copyRecording()),
      chip('비우기', ar !== null, () => {
        ar?.clearRecording();
        showNotice('기록을 비웠습니다', 'ok', REC_NOTICE_MS);
      }),
    ]),
  );
  panel.append(chipRow('fov', [chip('calib', ar !== null, calibrateFov)]));
  panel.append(
    chipRow('config', [
      // BASE_URL, not '/settings/'. Under the /sar/npay-ar/ deploy a domain-absolute path leaves
      // the demo entirely and lands on whatever owns the domain root. '?reset=1' is relative and
      // therefore already correct.
      chip('settings', true, () => location.assign(`${import.meta.env.BASE_URL}settings/`)),
      chip('reset', true, () => location.assign('?reset=1')),
    ]),
  );
  return panel;
}

function chipRow(label: string, chips: HTMLElement[]): HTMLElement {
  const row = el('div');
  row.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;align-items:center;margin-bottom:4px';
  const tag = el('span');
  tag.textContent = `${label}:`;
  tag.style.cssText = 'opacity:.6;min-width:56px';
  row.append(tag, ...chips);
  return row;
}

function chip(text: string, enabled: boolean, onClick: () => void): HTMLElement {
  const b = document.createElement('button');
  b.textContent = text;
  b.disabled = !enabled;
  b.style.cssText =
    `font:inherit;padding:3px 6px;border-radius:4px;border:1px solid #2a6;` +
    `background:${enabled ? '#0b3' : '#333'};color:${enabled ? '#032' : '#777'};` +
    `cursor:${enabled ? 'pointer' : 'default'}`;
  b.addEventListener('click', onClick);
  return b;
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function el(tag = 'div', className?: string): HTMLDivElement {
  const node = document.createElement(tag) as HTMLDivElement;
  if (className !== undefined) node.className = className;
  return node;
}

function title(en: string, ko: string): HTMLElement {
  const box = el();
  const a = el();
  a.textContent = en;
  a.style.cssText = 'font:800 34px/1.1 var(--font);letter-spacing:.06em';
  const b = el();
  b.textContent = ko;
  b.style.cssText = 'font:800 30px/1.2 var(--font)';
  box.append(a, b);
  return box;
}

function heading(text: string): HTMLElement {
  const h = el();
  h.textContent = text;
  h.style.cssText = 'font:700 21px/1.4 var(--font);white-space:pre-line';
  return h;
}

function caption(text: string): HTMLElement {
  const c = el('div', 'caption');
  c.textContent = text;
  return c;
}

/** Caption for the AR states: bottom-anchored, on a legibility plate, never interactive. */
function bottomCaption(text: string): HTMLElement {
  const plate = el('div', 'caption-plate');
  plate.append(caption(text));
  return plate;
}

function button(label: string, onClick: () => void, ghost = false, disabled = false): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = ghost ? 'btn btn--ghost' : 'btn';
  b.textContent = label;
  b.disabled = disabled;
  if (disabled) b.style.opacity = '0.4';
  b.addEventListener('click', onClick);
  return b;
}

function showNotice(text: string, variant: 'error' | 'ok' = 'error', autoDismissMs?: number): void {
  const n = el('div', variant === 'ok' ? 'notice notice--ok' : 'notice');
  n.textContent = text;
  n.addEventListener('click', () => n.remove());
  document.body.append(n);
  // Errors stay until dismissed; a confirmation that outlives the screen it confirms is noise.
  if (autoDismissMs !== undefined) window.setTimeout(() => n.remove(), autoDismissMs);
}

async function copyAppUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(config.APP_URL);
    showNotice(config.COPY.unsupported.copied);
  } catch {
    showNotice(config.APP_URL);
  }
}

// Must stay last: see the comment on bootstrap().
bootstrap();
